import ArrayUtils from "./ArrayUtils.js";
import { populationRank } from "./QuestionWarmup.js";
import { getConfusionPool } from "./FlagConfusionGroups.js";
import { CONTINENTS } from "./Continents.js";

/**
 * Questions du Défi du jour.
 *
 * Version 1 (jusqu'à la veille de `V2_START`) : 20 drapeaux tirés au hasard,
 * distracteurs au hasard. 56 % des parties Classic finissaient à 20/20 : les
 * pays rares tombaient au milieu de propositions faciles à éliminer, et la
 * grille partagée, toute verte, ne racontait rien.
 *
 * Version 2 :
 * - une difficulté croissante : 5 pays parmi les 50 plus peuplés, 7 parmi les
 *   70 suivants, 8 parmi les autres ;
 * - des distracteurs à la hauteur : au hasard pour les questions faciles, du
 *   même continent ensuite, et pour les drapeaux difficiles des drapeaux qui se
 *   ressemblent (les groupes de Flag Nerd Tricky) ;
 * - un thème par jour de la semaine : drapeaux, capitales, ou mélange ;
 * - le même tirage dans les deux langues (pays triés par code avant le
 *   mélange : les fichiers FR et EN ne sont pas dans le même ordre, et la v1
 *   donnait des questions différentes selon la langue).
 *
 * Une partie commencée garde la version de son début (voir DailyChallenge) :
 * une mise en ligne en pleine journée ne change pas les questions d'un joueur
 * déjà lancé.
 */

/** Premier jour (UTC) du Défi en version 2. */
export const V2_START = "2026-10-05";

export const TOTAL_QUESTIONS = 20;

/** Paliers de difficulté (rang de population) et nombre de questions par palier. */
const TIERS = [
    { maxRank: 50, count: 5 },
    { maxRank: 120, count: 7 },
    { maxRank: Infinity, count: 8 },
];

/** Thème par jour de la semaine UTC (0 = dimanche). */
const THEMES = ["flags", "flags", "capitals", "flags", "capitals", "flags", "mix"];

export function versionFor(dateStr) {
    return dateStr >= V2_START ? 2 : 1;
}

/** Thème du jour : "flags", "capitals" ou "mix" (toujours "flags" en v1). */
export function themeFor(dateStr) {
    if (versionFor(dateStr) === 1) return "flags";
    return THEMES[new Date(`${dateStr}T00:00:00Z`).getUTCDay()];
}

/**
 * @param {object} args
 * @param {Array}  args.countries  pays de la langue courante ({ code, name, capital })
 * @param {number} args.seed       graine du jour (et du mode)
 * @param {"classic"|"hard"} args.mode
 * @param {string} args.date       `YYYY-MM-DD` (UTC)
 * @param {number} [args.version]  forcée par une partie reprise
 * @returns {Array<{country, type: "flag"|"capital", proposals?: Array}>}
 */
export function generateDailyQuestions({ countries, seed, mode, date, version = versionFor(date) }) {
    if (version === 1) return generateV1(countries, seed, mode);
    return generateV2(countries, seed, mode, themeFor(date));
}

/** Version 1, conservée à l'identique pour les parties commencées avant la bascule. */
function generateV1(countries, seed, mode) {
    const shuffled = ArrayUtils.seededShuffle(countries, seed);
    const selected = shuffled.slice(0, TOTAL_QUESTIONS);

    if (mode === "hard") return selected.map(country => ({ country, type: "flag" }));

    const rng = ArrayUtils.seededRandom(seed + 1000);
    return selected.map(country => {
        const proposals = [{ code: country.code, name: country.name }];
        while (proposals.length < 4) {
            const candidate = countries[Math.floor(rng() * countries.length)];
            if (!proposals.find(p => p.code === candidate.code)) {
                proposals.push({ code: candidate.code, name: candidate.name });
            }
        }
        seededShuffleInPlace(proposals, rng);
        return { country, type: "flag", proposals };
    });
}

function generateV2(countries, seed, mode, theme) {
    return buildQuiz({ countries, seed, mode, theme, tiers: TIERS });
}

/**
 * Tirage d'un quiz à difficulté croissante, reproductible à partir de sa graine :
 * le Défi du jour, et le Défi un ami (même graine dans le lien = mêmes questions).
 * @param {object} args
 * @param {Array<{maxRank: number, count: number}>} args.tiers  paliers de population
 * @param {"flags"|"capitals"|"mix"} args.theme
 */
export function buildQuiz({ countries, seed, mode, theme, tiers }) {
    const total = tiers.reduce((sum, tier) => sum + tier.count, 0);
    const canonical = [...countries].sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
    const shuffled = ArrayUtils.seededShuffle(canonical, seed);
    const rng = ArrayUtils.seededRandom(seed + 1000);

    const selected = [];
    let minRank = 0;
    tiers.forEach((tier, level) => {
        shuffled
            .filter(c => {
                const rank = populationRank(c.code);
                return rank >= minRank && rank < tier.maxRank;
            })
            .slice(0, tier.count)
            .forEach(country => selected.push({ country, level }));
        minRank = tier.maxRank;
    });
    // Filet de sécurité : un pays sans rang de population (données incomplètes)
    // ne doit pas laisser le défi à moins de 20 questions.
    for (const country of shuffled) {
        if (selected.length >= total) break;
        if (!selected.some(s => s.country.code === country.code)) selected.push({ country, level: tiers.length - 1 });
    }

    return selected.map(({ country, level }) => {
        const type = theme === "capitals" ? "capital"
            : theme === "mix" ? (rng() < 0.5 ? "flag" : "capital")
            : "flag";
        if (mode === "hard") return { country, type };
        return { country, type, proposals: buildProposals(country, type, level, canonical, rng) };
    });
}

/**
 * Trois distracteurs : au hasard au palier facile, du même continent ensuite,
 * et pour un drapeau du palier difficile, d'abord des drapeaux qui lui
 * ressemblent. Les capitales en double (aucune aujourd'hui) sont écartées pour
 * qu'une proposition ne puisse jamais être juste deux fois.
 */
export function buildProposals(country, type, level, canonical, rng) {
    const entry = c => (type === "capital"
        ? { code: c.code, name: c.name, capital: c.capital }
        : { code: c.code, name: c.name });
    const proposals = [entry(country)];
    const taken = c => proposals.some(p => p.code === c.code || (type === "capital" && p.capital === c.capital));

    const pools = [];
    if (level >= 2 && type === "flag") {
        const lookalikes = getConfusionPool(country.code);
        pools.push(canonical.filter(c => lookalikes.has(c.code)));
    }
    if (level >= 1) {
        const zone = CONTINENTS[country.code];
        pools.push(canonical.filter(c => zone && CONTINENTS[c.code] === zone));
    }
    pools.push(canonical);

    for (const pool of pools) {
        const candidates = pool.filter(c => !taken(c));
        while (proposals.length < 4 && candidates.length) {
            const [pick] = candidates.splice(Math.floor(rng() * candidates.length), 1);
            proposals.push(entry(pick));
        }
        if (proposals.length >= 4) break;
    }

    seededShuffleInPlace(proposals, rng);
    return proposals;
}

function seededShuffleInPlace(array, rng) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
}
